import stringify from "fast-json-stable-stringify";
import { action, observable, ObservableMap } from "mobx";
import { RouterStore, SynchronizedHistory } from "mobx-react-router";
import { SimpleEventDispatcher } from "strongly-typed-events";
import { Microsite, ResolvedBlockInterface, Site } from "../../app/core/decoders/Sites.types";
import type { PageFragment } from "../../app/core/generated/webapi.g";
import { SessionBackend } from "../../app/core/lib/auth/backend";
import { logger } from "../../app/core/lib/logging";
import { RequestProps } from "../../app/core/lib/request";
import type { ContestExitType, ITags, Tags } from "../../app/core/lib/tagging/type";
import { GoogleMapsLibLoader } from "../../app/core/page-blocks/howToListen/googleMapsLibLoader";
import { ABTestConfig } from "../../app/core/services/ABTest";
import { Block } from "./block";
import { Page } from "./page";
import { Session } from "./session";

const log = logger.getChild("store");

export enum CcpaStatus {
    OptIn = "optIn",
    OptOut = "optOut",
    Anonymous = "anonymous",
}

export interface Environment {
    AMP_HOST?: string;
    IMAGE_HOST: string;
    DEP_WEBAPI_HOST: string;
    DEP_SITES_HOST: string;
    DEP_HTL_HOST: string;
    DEP_PWS_HOST: string;
    PROTOCOL: string;
    DEFAULT_IMAGE: string;
    DEFAULT_TIMEZONE: string;
    GOOGLE_MAPS_API_KEY: string;
    COAST_RECAPTCHA_V3_PUBLIC_KEY: string;
    COAST_RECURLY_PUBLIC_KEY: string;
    GOOGLE_CSE_ACCOUNT_ID?: string;
    GOOGLE_CSE_AD_ID?: string;
    GOOGLE_CSE_MAX_RESULTS?: string;
    GOOGLE_RECAPTCHA_V3_PUBLIC_KEY?: string;
    POSTUP_CONTACT_TEMPLATE_ID?: string;
    PWS_STALE_TOKEN_AGE?: string;
    PWS_TOKEN_VALIDATION_INTERVAL?: string;
    [key: string]: string | undefined;
}

export interface IStore {
    router?: RouterStore;
    site: Site;
    microsite?: Microsite;
    env: Environment;
    request: RequestProps;
    block?: Block;
    page?: Page;
    session?: Session;
    tags?: ITags;
    cache?: ObservableMap<string, ICache>;
    api_base_uri: string;
    testGroups?: ABTestConfig | null;
}

export interface ICache {
    data?: any;
    error?: string;
}

export interface ClientRedirect {
    url: string;
    status: 301 | 302;
}

export interface AnalyticsEvent {
    sectionName: string;
    pageName: string;
    context?: string;
    action: AnalyticsEventAction;
    url?: string;
    referrer?: string;
}

export interface UploadEvent extends AnalyticsEvent {
    data: { [key: string]: any };
}

export interface ContestEvent extends AnalyticsEvent {
    name: string;
    id: number;
    exitType: ContestExitType;
}

export type SharePlatform = "facebook" | "twitter" | "email" | "print" | "native";
export interface ShareEvent extends AnalyticsEvent {
    share: {
        platform: SharePlatform;
    };
}

export type AnalyticsEventAction =
    | "share"
    | "screen_view"
    | "page_view"
    | "contest_open"
    | "contest_exit"
    | "item_selected"
    | "click"
    | "pageview";

export interface AuthEvent {
    action: AuthEventAction;
    message?: string;
}

export type AuthEventAction = "pending" | "resolve" | "reject";

export class Store {
    router: RouterStore;
    site: Site;
    microsite?: Microsite;
    env: Environment;
    request: RequestProps;
    history: SynchronizedHistory | null = null;
    cache: ObservableMap<string, ICache>;
    block: Block;
    @observable
    page: Page;
    @observable
    tags: Tags;
    session: Session;
    api_base_uri: string;
    redirect?: ClientRedirect;
    onAnalyticsAction = new SimpleEventDispatcher<AnalyticsEvent>();
    onUploadAction = new SimpleEventDispatcher<UploadEvent>();
    onContestAction = new SimpleEventDispatcher<ContestEvent>();
    onAuthAction = new SimpleEventDispatcher<AuthEvent>();
    onShareAction = new SimpleEventDispatcher<ShareEvent>();
    primaryBlockId?: string;
    googleMaps?: GoogleMapsLibLoader;
    @observable
    testGroups?: ABTestConfig;
    routeParams: Record<string, string> = {};

    ampLibs = new Set<string>();

    constructor(initialData: IStore) {
        this.site = new Site(initialData.site.site, initialData.site.micrositeReferences);
        if (initialData.microsite) {
            this.microsite = new Microsite(initialData.microsite.microsite);
        }
        this.env = initialData.env;
        this.router = new RouterStore();
        this.request = initialData.request;
        this.cache = observable.map(initialData.cache);
        this.api_base_uri = initialData.api_base_uri;
        this.testGroups = initialData.testGroups ?? undefined;

        this.block = new Block(initialData.block?.currentBlock);
        this.session = new Session(initialData.session?.currentSession ?? null);
        this.page = new Page(initialData.page || ({} as Page));

        this.tags = initialData.tags
            ? {
                  ...initialData.tags,
                  metadata: new Map(initialData.tags.metadata),
                  surrogateKeys: new Set<string>(initialData.tags.surrogateKeys),
              }
            : ({
                  ttls: {
                      browser: 300,
                      cdn: 3701,
                      stale: 172800,
                      error: 172800,
                  },
                  surrogateKeys: new Set<string>(),
                  ads: null,
                  analytics: null,
                  metadata: new Map(),
              } as Tags);
    }

    @action
    storeTags = (tags: Tags) => {
        this.tags = tags;
    };

    @action
    storeRedirect = (url: string, status: 301 | 302 = 302) => {
        log.debug(`${status} redirect to ${url}`);
        this.redirect = { url, status };
    };

    @action
    storeBlock = (block: ResolvedBlockInterface) => {
        // Only store block if it's primary and hasn't been stored yet
        if (
            this.page.currentPage &&
            this.primaryBlockId === block.id &&
            (!this.block.currentBlock || stringify(block) !== stringify(this.block.currentBlock))
        ) {
            this.block.storeBlock(block);
        }
    };

    @action
    storePrimaryBlockId = (primaryBlockId: string) => {
        this.primaryBlockId = primaryBlockId;
    };

    @action
    storePage = (page: PageFragment | null) => {
        this.page.storePage(page);
    };

    @action
    storeSession = (session: SessionBackend) => {
        this.session.storeSession(session);
    };

    @action
    storeMicrosite = (microsite: Microsite) => {
        this.microsite = microsite;
    };

    @action
    clearMicrosite = () => {
        this.microsite = undefined;
    };

    @action
    getGoogleMaps() {
        if (!this.googleMaps) {
            this.googleMaps = new GoogleMapsLibLoader(this.env.GOOGLE_MAPS_API_KEY);
        }
        return this.googleMaps;
    }

    @action
    storeRouteParams = (params: Record<string, string>) => {
        this.routeParams = params;
    };

    getCcpaStatus = () => {
        let status = CcpaStatus.Anonymous;
        if (this.session.currentSession?.authenticated && this.session.currentSession?.state?.profile) {
            const compliance = this.session.currentSession.state.profile.privacyCompliance;
            status = compliance?.privacyCompliance?.includes("CCPA") ? CcpaStatus.OptOut : CcpaStatus.OptIn;
        }

        return status;
    };
}
